import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { 
  FileText, Plus, Search, PenTool, Upload, FolderOpen,
  FileSignature, Clock, CheckCircle2, XCircle, Eye
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const DOC_TYPES = [
  { value: "contract", label: "Contrato" },
  { value: "policy", label: "Política" },
  { value: "letter", label: "Carta" },
  { value: "receipt", label: "Recibo" },
  { value: "certificate", label: "Certificado" },
  { value: "other", label: "Otro" },
];

const STATUS_BADGES = {
  active: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  signed: "bg-blue-100 text-blue-700",
  expired: "bg-red-100 text-red-700",
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [signRequests, setSignRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [tab, setTab] = useState("documents");
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [newDoc, setNewDoc] = useState({ title: "", doc_type: "contract", employee_id: "", content: "" });
  const [newTemplate, setNewTemplate] = useState({ name: "", doc_type: "contract", content: "" });
  const [newSign, setNewSign] = useState({ document_id: "", signer_id: "", message: "" });

  const headers = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const opts = { headers: headers(), withCredentials: true };
      const [docRes, tempRes, empRes] = await Promise.all([
        axios.get(`${API}/documents`, opts),
        axios.get(`${API}/document-templates`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/employees`, opts).catch(() => ({ data: [] })),
      ]);
      setDocuments(docRes.data || []);
      setTemplates(tempRes.data || []);
      setEmployees(empRes.data || []);
    } catch {
      toast.error("Error al cargar documentos");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateDoc = async () => {
    if (!newDoc.title) { toast.error("Título requerido"); return; }
    try {
      await axios.post(`${API}/documents`, newDoc, { headers: headers(), withCredentials: true });
      toast.success("Documento creado");
      setShowDocDialog(false);
      setNewDoc({ title: "", doc_type: "contract", employee_id: "", content: "" });
      fetchData();
    } catch { toast.error("Error al crear documento"); }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name) { toast.error("Nombre requerido"); return; }
    try {
      await axios.post(`${API}/document-templates`, newTemplate, { headers: headers(), withCredentials: true });
      toast.success("Plantilla creada");
      setShowTemplateDialog(false);
      setNewTemplate({ name: "", doc_type: "contract", content: "" });
      fetchData();
    } catch { toast.error("Error al crear plantilla"); }
  };

  const handleSignRequest = async () => {
    if (!newSign.document_id || !newSign.signer_id) { toast.error("Datos requeridos"); return; }
    try {
      await axios.post(`${API}/documents/sign-request`, newSign, { headers: headers(), withCredentials: true });
      toast.success("Solicitud de firma enviada");
      setShowSignDialog(false);
      setNewSign({ document_id: "", signer_id: "", message: "" });
    } catch { toast.error("Error al solicitar firma"); }
  };

  const filteredDocs = documents.filter(d =>
    (d.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.doc_type || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout title="Gestión Documental">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Gestión Documental"
      subtitle="Documentos, plantillas y firma electrónica"
      actions={
        <div className="flex gap-2">
          <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-document-btn"><Plus className="w-4 h-4 mr-2" /> Documento</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuevo Documento</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Título</Label>
                  <Input value={newDoc.title} onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })} data-testid="doc-title-input" />
                </div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={newDoc.doc_type} onValueChange={(v) => setNewDoc({ ...newDoc, doc_type: v })}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Empleado (opcional)</Label>
                  <Select value={newDoc.employee_id} onValueChange={(v) => setNewDoc({ ...newDoc, employee_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.first_name} {e.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Contenido</Label>
                  <Textarea rows={4} value={newDoc.content} onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })} data-testid="doc-content-input" />
                </div>
                <Button onClick={handleCreateDoc} className="w-full" data-testid="save-document-btn">Guardar Documento</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="sign-request-btn"><PenTool className="w-4 h-4 mr-2" /> Solicitar Firma</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Solicitar Firma Electrónica</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Documento</Label>
                  <Select value={newSign.document_id} onValueChange={(v) => setNewSign({ ...newSign, document_id: v })}>
                    <SelectTrigger data-testid="sign-doc-select"><SelectValue placeholder="Seleccionar documento" /></SelectTrigger>
                    <SelectContent>
                      {documents.map(d => (
                        <SelectItem key={d.document_id} value={d.document_id}>{d.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Firmante</Label>
                  <Select value={newSign.signer_id} onValueChange={(v) => setNewSign({ ...newSign, signer_id: v })}>
                    <SelectTrigger data-testid="sign-signer-select"><SelectValue placeholder="Seleccionar firmante" /></SelectTrigger>
                    <SelectContent>
                      {employees.map(e => (
                        <SelectItem key={e.employee_id} value={e.employee_id}>
                          {e.first_name} {e.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Mensaje (opcional)</Label>
                  <Textarea rows={2} value={newSign.message} onChange={(e) => setNewSign({ ...newSign, message: e.target.value })} />
                </div>
                <Button onClick={handleSignRequest} className="w-full" data-testid="send-sign-btn">Enviar Solicitud</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6" data-testid="docs-tabs">
          <TabsTrigger value="documents"><FolderOpen className="w-4 h-4 mr-2" /> Documentos</TabsTrigger>
          <TabsTrigger value="templates"><FileText className="w-4 h-4 mr-2" /> Plantillas</TabsTrigger>
          <TabsTrigger value="signatures"><FileSignature className="w-4 h-4 mr-2" /> Firmas</TabsTrigger>
        </TabsList>

        <TabsContent value="documents">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Buscar documentos..." className="pl-10" value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} data-testid="docs-search" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredDocs.length === 0 ? (
                <div className="text-center py-12 text-slate-500" data-testid="docs-empty">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No hay documentos</p>
                  <p className="text-sm mt-1">Crea tu primer documento para empezar</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDocs.map((doc) => (
                      <TableRow key={doc.document_id} data-testid={`doc-row-${doc.document_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{doc.title}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_BADGES[doc.status] || "bg-slate-100 text-slate-600"}>{doc.status}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{doc.created_at?.slice(0, 10)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <div className="flex justify-end mb-4">
            <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-template-btn"><Plus className="w-4 h-4 mr-2" /> Nueva Plantilla</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nueva Plantilla</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Nombre</Label>
                    <Input value={newTemplate.name} onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })} data-testid="template-name-input" />
                  </div>
                  <div>
                    <Label>Tipo</Label>
                    <Select value={newTemplate.doc_type} onValueChange={(v) => setNewTemplate({ ...newTemplate, doc_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Contenido de la plantilla</Label>
                    <Textarea rows={6} value={newTemplate.content} onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })} data-testid="template-content-input" />
                  </div>
                  <Button onClick={handleCreateTemplate} className="w-full" data-testid="save-template-btn">Guardar Plantilla</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {templates.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500" data-testid="templates-empty">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No hay plantillas</p>
                <p className="text-sm mt-1">Crea plantillas para agilizar la generación de documentos</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((t) => (
                <Card key={t.template_id} className="card-hover" data-testid={`template-card-${t.template_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-semibold text-slate-900">{t.name}</p>
                        <Badge variant="outline" className="text-xs">{DOC_TYPES.find(dt => dt.value === t.doc_type)?.label || t.doc_type}</Badge>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{t.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="signatures">
          <Card>
            <CardContent className="p-8 text-center text-slate-500" data-testid="signatures-section">
              <FileSignature className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Firmas Electrónicas</p>
              <p className="text-sm mt-1">Las solicitudes de firma se envían desde la pestaña de Documentos</p>
              <p className="text-sm mt-1">Usa el botón "Solicitar Firma" para iniciar un proceso de firma</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
};

export default Documents;
